(() => {
  const scope = '#MainContent, .mega-menu, [data-cart-drawer-content], [data-product-modal-content], .wo-photo, [data-predictive-search-results]';
  const excluded = '[data-image-loading="off"], .hero, [data-hero], .editorial-floral-hero, .operator-tool-page, [data-design-studio], .native-design-studio, .design-studio, .studio-canvas, .campaign-lab-page, .product-swatches, .payment-icons';
  const records = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const config = document.querySelector('[data-image-loading-config]')?.dataset || {};
  const targets = new Map();
  const resize = new ResizeObserver(entries => {
    const changed = new Set();
    entries.forEach(entry => targets.get(entry.target)?.forEach(state => changed.add(state)));
    changed.forEach(position);
  });
  const visibility = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = records.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting;
      state.overlay?.classList.toggle('is-visible', state.visible);
      if (state.visible && !state.timer && state.status === 'loading') {
        state.timer = setTimeout(() => finish(state, false), 30000);
      }
      if (state.visible && state.status === 'error') scheduleRetry(state);
    });
  });

  function watch(target, state) {
    if (!targets.has(target)) {
      targets.set(target, new Set());
      resize.observe(target);
    }
    targets.get(target).add(state);
  }

  function unwatch(target, state) {
    const states = targets.get(target);
    states?.delete(state);
    if (!states?.size) {
      resize.unobserve(target);
      targets.delete(target);
    }
  }

  function position(state) {
    if (!state.overlay || !state.img.isConnected) return;
    const image = state.img.getBoundingClientRect();
    const host = state.mount.getBoundingClientRect();
    const scaleX = host.width / state.mount.offsetWidth || 1;
    const scaleY = host.height / state.mount.offsetHeight || 1;
    Object.assign(state.overlay.style, {
      left: `${(image.left - host.left) / scaleX - state.mount.clientLeft + state.mount.scrollLeft}px`,
      top: `${(image.top - host.top) / scaleY - state.mount.clientTop + state.mount.scrollTop}px`,
      width: `${image.width / scaleX}px`,
      height: `${image.height / scaleY}px`
    });
  }

  function removeOverlay(state) {
    state.overlay?.remove();
    state.overlay = null;
    unwatch(state.img, state);
    unwatch(state.mount, state);
    visibility.unobserve(state.img);
    clearTimeout(state.timer);
    clearTimeout(state.retryTimer);
    state.timer = null;
    state.retryTimer = null;
    if (![...records.values()].some(other => other !== state && other.mount === state.mount && other.overlay)) {
      state.mount.classList.remove('fs-image-host');
    }
  }

  function scheduleRetry(state) {
    if (!state.visible || state.retryTimer || state.retries >= 2 || !state.img.isConnected) return;
    state.retries++;
    state.retryTimer = setTimeout(() => {
      state.retryTimer = null;
      if (state.status !== 'error' || !state.img.isConnected) return;
      const src = state.img.getAttribute('src');
      const srcset = state.img.getAttribute('srcset');
      state.img.loading = 'eager';
      if (src) state.img.src = src;
      if (srcset) state.img.srcset = srcset;
      start(state);
    }, 800 * state.retries);
  }

  function placeholder(state) {
    if (state.secondary || state.overlay) return;
    if (getComputedStyle(state.mount).position === 'static') state.mount.classList.add('fs-image-host');
    const overlay = document.createElement('span');
    overlay.className = 'fs-image-placeholder';
    overlay.setAttribute('aria-hidden', 'true');
    state.mount.append(overlay);
    state.overlay = overlay;
    watch(state.mount, state);
    watch(state.img, state);
    position(state);
    visibility.observe(state.img);
  }

  function finish(state, success) {
    if (!state.img.isConnected || records.get(state.img) !== state) return;
    clearTimeout(state.timer);
    state.timer = null;
    if (success) {
      state.retries = 0;
      const animate = state.status === 'loading' && state.overlay?.classList.contains('is-visible') && !reducedMotion.matches;
      state.status = 'ready';
      state.img.dataset.fsImageState = 'ready';
      if (animate) {
        const overlay = state.overlay;
        overlay.classList.remove('is-visible');
        overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease-out', fill: 'forwards' }).finished
          .then(() => {
            if (state.overlay === overlay) removeOverlay(state);
          })
          .catch(() => {
            if (state.overlay === overlay) removeOverlay(state);
          });
      } else {
        removeOverlay(state);
      }
      return;
    }
    state.status = 'error';
    state.img.dataset.fsImageState = 'error';
    placeholder(state);
    if (!state.overlay) return;
    state.overlay.classList.add('is-error');
    state.overlay.removeAttribute('aria-hidden');
    const message = document.createElement('span');
    message.textContent = config.imageUnavailable || 'Image unavailable';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = config.imageRetry || 'Retry image';
    retry.setAttribute('aria-label', `${retry.textContent}: ${state.img.alt || message.textContent}`);
    retry.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const src = state.img.getAttribute('src');
      const srcset = state.img.getAttribute('srcset');
      state.retries = 0;
      state.img.loading = 'eager';
      if (src) state.img.src = src;
      if (srcset) state.img.srcset = srcset;
      start(state);
    });
    state.overlay.replaceChildren(message, retry);
    scheduleRetry(state);
  }

  async function loaded(state) {
    const generation = state.generation;
    const source = state.img.currentSrc || state.img.src;
    if (!state.img.naturalWidth) {
      finish(state, false);
      return;
    }
    try {
      await state.img.decode();
    } catch {
      if (generation === state.generation && source === (state.img.currentSrc || state.img.src)) finish(state, false);
      return;
    }
    if (generation === state.generation && source === (state.img.currentSrc || state.img.src)) finish(state, true);
  }

  function start(state) {
    state.generation++;
    removeOverlay(state);
    if (state.img.complete && state.img.naturalWidth > 0) {
      state.status = 'ready';
      state.img.dataset.fsImageState = 'ready';
      return;
    }
    state.status = 'loading';
    state.img.dataset.fsImageState = 'loading';
    placeholder(state);
    if (state.img.complete && state.img.currentSrc) finish(state, false);
  }

  function register(img) {
    if (records.has(img) || !img.closest(scope) || img.closest(excluded)) return;
    if (!img.getAttribute('src') && !img.getAttribute('srcset')) return;
    if (img.getAttribute('aria-hidden') === 'true' || /\.svg(?:[?#]|$)/i.test(img.currentSrc || img.src)) return;
    if (img.fetchPriority === 'high' || img.loading === 'eager') return;
    if (img.width > 0 && img.height > 0 && Math.max(img.width, img.height) < 48) return;
    const interactive = img.closest('a, button');
    const mount = interactive ? interactive.parentElement : img.parentElement?.closest('picture')?.parentElement || img.parentElement;
    if (!mount) return;
    const card = img.closest('.product-card__image');
    const state = { img, mount, generation: 0, retries: 0, retryTimer: null, secondary: card && img !== card.querySelector('img') };
    records.set(img, state);
    state.onLoad = () => loaded(state);
    state.onError = () => finish(state, false);
    img.addEventListener('load', state.onLoad);
    img.addEventListener('error', state.onError);
    start(state);
  }

  function scan(root) {
    if (root instanceof HTMLImageElement) register(root);
    root.querySelectorAll?.('img').forEach(register);
  }

  const mutations = new MutationObserver(entries => {
    const restart = new Set();
    let removed = false;
    entries.forEach(entry => {
      if (entry.type === 'childList') {
        entry.addedNodes.forEach(scan);
        removed ||= entry.removedNodes.length > 0;
      } else {
        const img = entry.target instanceof HTMLImageElement ? entry.target : entry.target.closest('picture')?.querySelector('img');
        if (!img) return;
        if (records.has(img)) restart.add(records.get(img));
        else register(img);
      }
    });
    restart.forEach(start);
    if (removed) records.forEach((state, img) => {
      if (img.isConnected) return;
      removeOverlay(state);
      img.removeEventListener('load', state.onLoad);
      img.removeEventListener('error', state.onError);
      records.delete(img);
    });
  });
  mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'media'] });
  scan(document.body);
})();
